import { showToast } from "@/lib/utils/toast";
import { reportingBoundaryService } from "../services/reporting-boundary.services";
import { reportingBoundaryStore } from "../stores/reporting-boundary.store";
import { reportingYearStore } from "../stores/reporting-year.store";

let reportingYearsRequest = null;
let reportingYearsLoaded = false;
let reportingYearsCache = [];
let availableFrameworksCache = [];

export const loadReportingBoundary = async () => {
  reportingBoundaryStore.setLoading(true);
  reportingBoundaryStore.clearFrameworksError();
  try {
    const [boundaryData, frameworkData] = await Promise.all([
      reportingBoundaryService.getReportingBoundary(),
      reportingBoundaryService.getFrameworks(),
    ]);

    if (frameworkData?.frameworks) {
      reportingBoundaryStore.setAvailableFrameworks(frameworkData.frameworks);
    } else {
      reportingBoundaryStore.setFrameworksError(
        "Failed to load available frameworks.",
      );
    }

    const hasConfig = !!boundaryData?.reportingBoundary;
    reportingBoundaryStore.setIsConfigured(hasConfig);

    if (hasConfig) {
      reportingBoundaryStore.hydrate(boundaryData.reportingBoundary);
    }
  } catch {
    showToast.error("Failed to load reporting boundary");
  } finally {
    reportingBoundaryStore.setLoading(false);
  }
};

export const loadReportingYears = async ({ force = false } = {}) => {
  if (!force && reportingYearsLoaded) {
    reportingYearStore.setReportingYears(reportingYearsCache);
    reportingYearStore.setAvailableFrameworks(availableFrameworksCache);
    return reportingYearsCache;
  }

  if (!force && reportingYearsRequest) {
    return reportingYearsRequest;
  }

  reportingBoundaryStore.setLoading(true);

  reportingYearsRequest = (async () => {
    const [reportingYearData, frameworkData] = await Promise.all([
      reportingBoundaryService.getReportingYearByCompany(),
      reportingBoundaryService.getFrameworks(),
    ]);

    if (frameworkData?.frameworks) {
      reportingYearStore.setAvailableFrameworks(frameworkData.frameworks);
      availableFrameworksCache = frameworkData.frameworks;
    } else {
      showToast.error("Failed to load available frameworks.");
    }

    const reportingYears = reportingYearData?.reportingYears ?? [];

    reportingYearStore.setReportingYears(reportingYears);
    reportingYearsCache = reportingYears;
    reportingYearsLoaded = true;

    return reportingYears;
  })();

  try {
    return await reportingYearsRequest;
  } catch (error) {
    showToast.error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to load reporting years",
    );

    return [];
  } finally {
    reportingYearsRequest = null;
    reportingBoundaryStore.setLoading(false);
  }
};

export const loadFrameworks = async () => {
  reportingBoundaryStore.setLoading(true);

  try {
    const frameworkData = await reportingBoundaryService.getFrameworks();

    const frameworks = frameworkData?.frameworks ?? [];

    if (frameworks.length > 0) {
      reportingYearStore.setAvailableFrameworks(frameworks);
    } else {
      showToast.error("Failed to load available frameworks.");
    }

    reportingYearStore.setAvailableFrameworks(frameworks);

    return frameworks;
  } catch (error) {
    showToast.error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to load frameworks",
    );

    return [];
  } finally {
    reportingBoundaryStore.setLoading(false);
  }
};

export const loadReportingYearsList = async () => {
  try {
    const data = await reportingBoundaryService.getReportingYearList();

    return data?.reportingYears ?? [];
  } catch {
    showToast.error("Failed to load reporting years");
    return [];
  }
};

export const getReportingYearById = async (reportingYearId) => {
  try {
    const data =
      await reportingBoundaryService.getReportingYearById(reportingYearId);

    return data?.reportingYear ?? null;
  } catch {
    showToast.error("Failed to load reporting year");
    return null;
  }
};

export const saveReportingBoundary = async () => {
  const {
    startYear,
    startMonth,
    endYear,
    endMonth,
    selectedFrameworkCodes,
    startYearError,
    endYearError,
  } = reportingBoundaryStore;

  if (startYearError) {
    showToast.error(startYearError);
    return false;
  }

  if (endYearError) {
    showToast.error(endYearError);
    return false;
  }

  reportingBoundaryStore.setSaving(true);
  try {
    const data = await reportingBoundaryService.updateReportingBoundary({
      startYear,
      startMonth,
      endYear,
      endMonth,
      frameworkCodes: selectedFrameworkCodes,
    });

    if (data?.reportingBoundary) {
      reportingBoundaryStore.hydrate(data.reportingBoundary);
    }

    showToast.success("Reporting boundary updated successfully");
    return true;
  } catch {
    showToast.error("Failed to update reporting boundary");
    return false;
  } finally {
    reportingBoundaryStore.setSaving(false);
  }
};

export const createReportingYear = async (formData) => {
  const {
    startYear,
    startMonth,
    endYear,
    endMonth,
    isAllOrganization,
    isCompanySelected,
    frameworks,
    organizations,
  } = formData;

  if (!startYear || !startMonth) {
    showToast.error("Start year & month is required.");
    return false;
  }

  if (!endYear || !endMonth) {
    showToast.error("End year & month is required.");
    return false;
  }

  if (
    endYear < startYear ||
    (endYear === startYear && endMonth <= startMonth)
  ) {
    showToast.error(
      "End month must be after start month when years are the same.",
    );
    return false;
  }

  try {
    const data = await reportingBoundaryService.createReportingYear({
      startYear,
      startMonth,
      endYear,
      endMonth,
      isAllOrganization,
      isCompanySelected,
      frameworkCodes: frameworks,
      organizations,
    });

    reportingYearsLoaded = false;
    reportingYearsCache = [];
    showToast.success("Reporting Year Created successfully");

    return data?.reportingBoundary ?? null;
  } catch {
    showToast.error("Failed to update reporting boundary");
    return false;
  }
};

export const updateReportingYear = async (reportingYearId, formData) => {
  const {
    startYear,
    startMonth,
    endYear,
    endMonth,
    isAllOrganization,
    isCompanySelected,
    frameworks,
    organizations,
  } = formData;

  if (!reportingYearId) {
    showToast.error("Reporting year ID is required.");
    return false;
  }

  if (!startYear || !startMonth) {
    showToast.error("Start year & month is required.");
    return false;
  }

  if (!endYear || !endMonth) {
    showToast.error("End year & month is required.");
    return false;
  }

  if (
    endYear < startYear ||
    (endYear === startYear && endMonth <= startMonth)
  ) {
    showToast.error(
      "End month must be after start month when years are the same.",
    );
    return false;
  }

  try {
    const payload = {
      startYear,
      startMonth,
      endYear,
      endMonth,
      isAllOrganization,
      isCompanySelected,
      frameworkCodes: frameworks,
      organizations: isAllOrganization ? [] : organizations,
    };

    const data = await reportingBoundaryService.updateReportingYear(
      reportingYearId,
      payload,
    );

    reportingYearsLoaded = false;
    reportingYearsCache = [];
    showToast.success("Reporting Year updated successfully");

    return data?.reportingBoundary ?? data?.reportingYear ?? null;
  } catch {
    showToast.error("Failed to update reporting year");
    return false;
  }
};

export const deleteReportingYear = async (id) => {
  if (!id) {
    showToast.error("Reporting year not found");
    return false;
  }

  try {
    await reportingBoundaryService.deleteReportingYear(id);

    reportingYearsLoaded = false;
    reportingYearsCache = [];
    showToast.success("Reporting year deleted successfully");
    return true;
  } catch {
    showToast.error("Failed to delete reporting year");
    return false;
  }
};
